import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, ShoppingBag, Menu, X, ChevronDown } from 'lucide-react';
import { navLinks, collections } from '../../data/mock';
import { settingsAPI, collectionsAPI } from '../../services/api';
import { useCart } from '../../context/CartContext';
import CurrencySwitcher from '../common/CurrencySwitcher';

const Header = () => {
  const { getItemCount, toggleCart } = useCart();
  const cartCount = getItemCount();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [collectionsDropdownOpen, setCollectionsDropdownOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState({
    announcement: 'Introducing Gorito Art Tees',
    announcement_link: '/collections/t-shirts'
  });
  const [collectionsList, setCollectionsList] = useState(collections);
  const navigate = useNavigate();

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await settingsAPI.get();
        if (settings) {
          setSiteSettings(settings);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    
    const loadCollections = async () => {
      try {
        const data = await collectionsAPI.getAll();
        if (data && data.length > 0) {
          // Sort collections: "Paintings" first, then alphabetically
          const sorted = [...data].sort((a, b) => {
            if (a.name === 'Paintings') return -1;
            if (b.name === 'Paintings') return 1;
            return a.name.localeCompare(b.name);
          });
          setCollectionsList(sorted);
        }
      } catch (error) {
        console.error('Error loading collections:', error);
      }
    };
    
    loadSettings();
    loadCollections();
  }, []);

  return (
    <>
      {/* Announcement Bar */}
      {siteSettings.announcement && (
        <div className="bg-gray-100 py-2 text-center">
          <Link 
            to={siteSettings.announcement_link || '/collections'} 
            className="text-sm text-gray-800 hover:text-gray-600 transition-colors flex items-center justify-center gap-2"
          >
            {siteSettings.announcement}
            <span className="text-gray-500">→</span>
          </Link>
        </div>
      )}

      {/* Main Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img 
                src="/logo.png" 
                alt="Gorito Fine Arts" 
                className="h-[75px] w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <div key={link.name} className="relative group">
                  {link.hasDropdown && link.name === 'Collections' ? (
                    <div
                      className="relative"
                      onMouseEnter={() => setCollectionsDropdownOpen(true)}
                      onMouseLeave={() => setCollectionsDropdownOpen(false)}
                    >
                      <Link 
                        to="/collections"
                        className="flex items-center gap-1 text-sm text-gray-700 hover:text-black transition-colors py-2"
                      >
                        {link.name}
                        <ChevronDown className="w-4 h-4" />
                      </Link>
                      {collectionsDropdownOpen && (
                        <div className="absolute top-full left-0 bg-white shadow-lg rounded-md py-2 min-w-[200px] border border-gray-100">
                          {collectionsList.map((collection) => (
                            <Link
                              key={collection.id}
                              to={`/collections/${collection.slug}`}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-black transition-colors"
                            >
                              {collection.name}
                            </Link>
                          ))}
                          <div className="border-t border-gray-100 mt-2 pt-2">
                            <Link
                              to="/collections"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-black transition-colors"
                            >
                              View All Collections
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : link.hasDropdown && link.name === 'About' ? (
                    <div className="relative group">
                      <Link 
                        to="/about"
                        className="flex items-center gap-1 text-sm text-gray-700 hover:text-black transition-colors py-2"
                      >
                        {link.name}
                        <ChevronDown className="w-4 h-4" />
                      </Link>
                      <div className="absolute top-full left-0 bg-white shadow-lg rounded-md py-2 min-w-[160px] border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                        <Link to="/about" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">About the Artist</Link>
                        <Link to="/about#process" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">My Process</Link>
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      className="text-sm text-gray-700 hover:text-black transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-4">
              {/* Currency Selector */}
              <CurrencySwitcher className="hidden lg:block" />

              {/* Search */}
              <button 
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 text-gray-700 hover:text-black transition-colors"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Account */}
              <Link to="/login" className="p-2 text-gray-700 hover:text-black transition-colors" title="Admin Login">
                <User className="w-5 h-5" />
              </Link>

              {/* Cart */}
              <button 
                onClick={toggleCart}
                className="p-2 text-gray-700 hover:text-black transition-colors relative"
                data-testid="cart-btn"
              >
                <ShoppingBag className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-black text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-gray-700"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        {searchOpen && (
          <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-200 py-4 px-4 shadow-lg">
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search artworks, collections..."
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  autoFocus
                />
              </div>
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <nav className="px-4 py-4 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="block text-gray-700 hover:text-black transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <div className="flex items-center justify-between py-2 border-t border-gray-100 pt-4">
                <span className="text-gray-700">Currency</span>
                <CurrencySwitcher />
              </div>
              <Link
                to="/admin"
                className="block text-gray-700 hover:text-black transition-colors py-2 border-t border-gray-100 pt-4"
                onClick={() => setMobileMenuOpen(false)}
              >
                Admin Dashboard
              </Link>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
